import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GlassCard } from '../ui/GlassCard';
import { 
  Store, 
  Globe, 
  Loader2, 
  MapPin, 
  CheckCircle2, 
  AlertCircle, 
  X,
  Phone,
  AlignLeft,
  ArrowRight,
  ChevronLeft,
  DollarSign,
  Languages,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useVenues } from '../../hooks/useVenues';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { AuthInput } from '../ui/AuthInput';
import { Select } from '../ui/Select';
import { SUPPORTED_LANGUAGES, getCountryCode, getCountryCities } from '../../lib/locales';

const COUNTRY_OPTIONS = [
  { value: 'Morocco', label: 'Morocco 🇲🇦', shortLabel: 'Morocco 🇲🇦', description: 'North Africa' },
  { value: 'United States', label: 'United States 🇺🇸', shortLabel: 'USA 🇺🇸', description: 'North America' },
  { value: 'United Kingdom', label: 'United Kingdom 🇬🇧', shortLabel: 'UK 🇬🇧', description: 'Europe' },
  { value: 'France', label: 'France 🇫🇷', shortLabel: 'France 🇫🇷', description: 'Europe' },
  { value: 'Spain', label: 'Spain 🇪🇸', shortLabel: 'Spain 🇪🇸', description: 'Europe' },
  { value: 'United Arab Emirates', label: 'United Arab Emirates 🇦🇪', shortLabel: 'UAE 🇦🇪', description: 'Middle East' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia 🇸🇦', shortLabel: 'Saudi Arabia 🇸🇦', description: 'Middle East' },
  { value: 'Canada', label: 'Canada 🇨🇦', shortLabel: 'Canada 🇨🇦', description: 'North America' },
];

const CURRENCY_OPTIONS = [
  { value: 'MAD', label: 'Moroccan Dirham (MAD)', shortLabel: 'MAD (Dirham)', description: 'MAD' },
  { value: 'USD', label: 'US Dollar ($)', shortLabel: 'USD ($)', description: 'USD' },
  { value: 'EUR', label: 'Euro (€)', shortLabel: 'EUR (€)', description: 'EUR' },
  { value: 'GBP', label: 'British Pound (£)', shortLabel: 'GBP (£)', description: 'GBP' },
  { value: 'AED', label: 'UAE Dirham (AED)', shortLabel: 'AED (Dirham)', description: 'AED' },
  { value: 'SAR', label: 'Saudi Riyal (SAR)', shortLabel: 'SAR (Riyal)', description: 'SAR' },
  { value: 'CAD', label: 'Canadian Dollar ($)', shortLabel: 'CAD ($)', description: 'CAD' },
];

// Full catalogue of supported languages available for operator menu creation
const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map(lang => ({
  value: lang.code,
  label: `${lang.label} (${lang.nativeLabel})`,
  description: lang.dir === 'rtl' ? 'RTL' : 'LTR',
}));

interface CreateVenueModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateVenueModal: React.FC<CreateVenueModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { venues, refreshVenues, setActiveVenue, organizationId } = useVenues();
  const { tierLimits } = useRoleAccess();
  
  const isAtLimit = venues.length >= tierLimits.venueCount;
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [country, setCountry] = useState('Morocco');
  const [city, setCity] = useState('Casablanca');
  const [currency, setCurrency] = useState('MAD');
  const [language, setLanguage] = useState('en');
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2 | 3>(1); // 1: Basic, 2: Details, 3: Success
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const slugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced slug availability check
  const checkSlugAvailability = useCallback((s: string) => {
    if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    if (!s || s.length < 2) { setSlugStatus('idle'); return; }
    setSlugStatus('checking');
    slugCheckTimer.current = setTimeout(async () => {
      const { data } = await supabase
        .from('venues')
        .select('id')
        .eq('slug', s)
        .maybeSingle();
      setSlugStatus(data ? 'taken' : 'available');
    }, 400);
  }, []);

  const handleCreate = async () => {
    if (!name || !slug) return;
    setIsLoading(true);
    setError(null);

    try {
      // Create venue + owner membership atomically via SECURITY DEFINER RPC
      const { data: venue, error: venueError } = await supabase.rpc('create_venue_with_owner', {
        p_name: name,
        p_slug: slug,
        p_phone: phone || null,
        p_address: address || null,
        p_description: description || null,
        p_primary_color: '#ff4f00',
        p_border_radius: '16',
        p_organization_id: organizationId || null,
        p_country: country,
        p_currency: currency,
        p_city: city.trim(),
      });

      if (venueError) throw venueError;

      // Initialize default and single enabled locale for newly created venue
      try {
        const primaryLocale = `${language}-${getCountryCode(country)}`;
        const venueId = venue && typeof venue === 'object' ? (venue as any).id : null;
        if (venueId) {
          await supabase
            .from('venues')
            .update({
              default_locale: primaryLocale,
              enabled_locales: [primaryLocale],
            })
            .eq('id', venueId);
        }
      } catch (localeErr) {
        console.error('Failed to set country locales in admin create:', localeErr);
      }

      // Refresh and Select
      await refreshVenues();
      
      // The RPC returns the venue object as JSON
      const newVenueId = venue && typeof venue === 'object' ? (venue as any).id : null;
      if (venue && typeof venue === 'object') {
        setActiveVenue(venue as any);
      }

      // Instruct OnboardingWidget to immediately present the interactive Welcome Onboarding Wizard
      if (newVenueId) {
        sessionStorage.setItem('tg_show_onboarding_wizard', newVenueId);
        sessionStorage.removeItem(`tg_wizard_seen_${newVenueId}`);
        localStorage.removeItem(`tg_onboarding_${newVenueId}`);
      }
      
      setStep(3); // Success step
      
      setTimeout(() => {
        onClose();
        // Reset state after closing
        setTimeout(() => {
          setStep(1);
          setName('');
          setSlug('');
          setPhone('');
          setAddress('');
          setDescription('');
          setCountry('Morocco');
          setCurrency('MAD');
        }, 300);
      }, 2500);

    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create venue';
      setError(message);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const generateSlug = (val: string) => {
    setName(val);
    const s = val.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setSlug(s);
    checkSlugAvailability(s);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-brand-dark"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-xl mx-auto"
          >
            <GlassCard className="overflow-hidden border-white/10 shadow-2xl shadow-black/50 p-0" intensity="dark">
              {/* Header */}
              <div className="px-8 py-6 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
                <div>
                  <h2 className="text-2xl font-black text-white tracking-tight flex items-center gap-3">
                    <Store className="text-brand-primary" size={24} />
                    {step === 2 ? 'Venue Details' : step === 3 ? 'Welcome!' : 'New Venue'}
                  </h2>
                  <p className="text-gray-500 text-sm font-medium mt-1">
                    {step === 1 ? 'Start by naming your location' : step === 2 ? 'Almost there! Add location info' : 'Your venue is ready'}
                  </p>
                </div>
                {step < 3 && (
                  <button 
                    onClick={onClose}
                    className="p-2 hover:bg-white/5 rounded-xl transition-colors text-gray-500 hover:text-white"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>

              <div className="max-h-[75vh] overflow-y-auto custom-scrollbar">
                <div className="p-8">
                  <AnimatePresence mode="wait">
                    {isAtLimit ? (
                      <motion.div
                        key="limit"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center py-8"
                      >
                        <div className="w-16 h-16 bg-brand-primary/10 rounded-2xl flex items-center justify-center text-brand-primary mx-auto mb-6 border border-brand-primary/20">
                          <AlertCircle size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2 italic">Venue Limit Reached</h3>
                        <p className="text-gray-400 text-sm font-medium mb-8 leading-relaxed">
                          Your current plan supports up to <span className="text-brand-primary font-black">{tierLimits.venueCount}</span> venues. 
                          Upgrade your plan to expand your organization.
                        </p>
                        <button 
                          onClick={() => {
                            onClose();
                            navigate('/admin/billing');
                          }}
                          className="w-full py-4 bg-brand-primary text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-xl shadow-brand-primary/20 hover:scale-[1.02] transition-all"
                        >
                          Upgrade Plan
                        </button>
                      </motion.div>
                    ) : step === 1 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        {error && (
                          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-start gap-3 text-red-400 text-sm">
                            <AlertCircle size={18} className="shrink-0" />
                            <p>{error}</p>
                          </div>
                        )}

                        <AuthInput
                          label="Venue Name"
                          icon={Store}
                          required
                          autoFocus
                          placeholder="e.g. Skyline Lounge"
                          value={name}
                          onChange={(e) => generateSlug(e.target.value)}
                        />

                        <div className="space-y-4">
                          <div className="relative group">
                            <AuthInput
                              label="Public URL Slug"
                              icon={Globe}
                              required
                              className="lowercase font-bold tracking-tight text-white"
                              placeholder="skyline-lounge"
                              value={slug}
                              error={slugStatus === 'taken' ? "This URL is already taken" : undefined}
                              onChange={(e) => {
                                const s = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                                setSlug(s);
                                checkSlugAvailability(s);
                              }}
                            />
                            
                            {slugStatus === 'checking' && (
                              <div className="absolute right-4 top-[41px]">
                                <Loader2 size={16} className="animate-spin text-brand-primary/50" />
                              </div>
                            )}
                          </div>

                          {/* URL Preview Card - MATCHING REGISTER UI */}
                          <div className="p-4 rounded-2xl bg-white/5 border border-white/5 flex items-center gap-3 transition-all group-focus-within:border-brand-primary/30">
                            <div className="w-10 h-10 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                              <Globe size={20} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Public Address</p>
                              <p className="text-sm font-mono truncate text-gray-400">
                                venue.tablegreet.com/<span className="text-brand-primary font-black">{slug || '...'}</span>
                              </p>
                            </div>
                          </div>
                        </div>

                        <motion.button 
                          whileHover={{ scale: 1.01, backgroundColor: '#ff5f1a' }}
                          whileTap={{ scale: 0.98 }}
                          disabled={!name || !slug || slugStatus !== 'available' || isLoading}
                          onClick={() => setStep(2)}
                          className="w-full py-4 bg-brand-primary rounded-xl font-black text-white flex items-center justify-center gap-3 shadow-xl shadow-brand-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all group mt-8"
                        >
                          Next Step <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                        </motion.button>
                      </motion.div>
                    )}

                    {step === 2 && (
                      <motion.div
                        key="step2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <AuthInput 
                            label="Contact Phone"
                            icon={Phone}
                            type="tel"
                            placeholder="+1 234 567 890"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                          />
                          <AuthInput 
                            label="Business Address"
                            icon={MapPin}
                            placeholder="Street, City, Postcode"
                            value={address}
                            onChange={e => setAddress(e.target.value)}
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <Select
                            label="Country"
                            options={COUNTRY_OPTIONS}
                            value={country}
                            onChange={(val) => {
                              setCountry(val);
                              if (val === 'Morocco') setCurrency('MAD');
                              else if (val === 'United States') setCurrency('USD');
                              else if (val === 'United Kingdom') setCurrency('GBP');
                              else if (val === 'France' || val === 'Spain') setCurrency('EUR');
                              else if (val === 'United Arab Emirates') setCurrency('AED');
                              else if (val === 'Saudi Arabia') setCurrency('SAR');
                              else if (val === 'Canada') setCurrency('CAD');
                              const cities = getCountryCities(val);
                              setCity(cities[0] || '');
                            }}
                            icon={<Globe size={18} />}
                          />
                          <Select
                            label="City"
                            options={getCountryCities(country).map((c: string) => ({ value: c, label: c, description: country }))}
                            value={city}
                            onChange={(val) => setCity(val)}
                            icon={<MapPin size={18} />}
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <Select
                            label="Currency"
                            options={CURRENCY_OPTIONS}
                            value={currency}
                            onChange={(val) => setCurrency(val)}
                            icon={<DollarSign size={18} />}
                          />
                          <Select
                            label="Menu Language"
                            options={LANGUAGE_OPTIONS}
                            value={language}
                            onChange={(val) => setLanguage(val)}
                            icon={<Languages size={18} />}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                            Short Description
                          </label>
                          <div className="relative">
                            <AlignLeft className="absolute left-4 top-4 text-gray-500" size={18} />
                            <textarea 
                              rows={3}
                              placeholder="Tell customers about your venue..."
                              className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-6 py-4 outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 transition-all text-white placeholder-gray-600 resize-none"
                              value={description}
                              onChange={e => setDescription(e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="flex gap-4 pt-4">
                          <button 
                            onClick={() => setStep(1)}
                            className="flex-1 py-4 bg-white/5 border border-white/10 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-white/10 transition-all text-gray-400 hover:text-white"
                          >
                            <ChevronLeft size={20} /> Back
                          </button>
                          <motion.button 
                            whileHover={{ scale: 1.01, backgroundColor: '#ff5f1a' }}
                            whileTap={{ scale: 0.98 }}
                            disabled={isLoading}
                            onClick={handleCreate}
                            className="flex-[2] py-4 bg-brand-primary rounded-xl font-black text-white flex items-center justify-center gap-3 shadow-xl shadow-brand-primary/20 group"
                          >
                            {isLoading ? <Loader2 size={20} className="animate-spin text-white" /> : (
                              <>Create Venue <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" /></>
                            )}
                          </motion.button>
                        </div>
                      </motion.div>
                    )}

                    {step === 3 && (
                      <motion.div
                        key="step3"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-12"
                      >
                        <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-500/30">
                          <CheckCircle2 size={40} className="text-emerald-500" />
                        </div>
                        <h3 className="text-3xl font-black text-white mb-2">Venue Created!</h3>
                        <p className="text-gray-400 max-w-xs mx-auto mb-8 font-medium">
                          \"{name}\" has been successfully initialized and added to your organization.
                        </p>
                        <p className="text-brand-primary font-bold text-sm animate-pulse italic">
                          Redirecting to dashboard...
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
